import { observer, Observer } from 'mobx-react-lite'

import User from 'singletons/User'
import Modals from 'singletons/Modals'
import AdditionalInfoCard from 'components/Dashboard/cards/AdditionalInfo'
import ScheduleCard from 'components/Dashboard/cards/Schedule'
import PatientCard from 'components/Dashboard/cards/Patient'
import Alert from 'components/Dashboard/cards/Alert'
import { statesByValue } from 'constants/states'

import { MedicalHistory, PhoneContained } from 'components/UI/svgIcons'
import VisitTypes from 'constants/visit-types'
import { useMemo } from 'react'
import TransmissionError from './TransmissionError/TransmissionError'
import { HELP_CENTER } from 'constants/routing'
import { useSelectCrisisStream } from 'hooks/crisis/useCrisisStream'
import PendingVisit from 'models/Provider/visits/PendingVisit'

const Home = () => {
  const { provider } = User

  const getVariant = useMemo(() => {
    return (visit) => {
      if (visit.claimed || !visit.canBeClaimed) {
        return visit.visitType === VisitTypes.success ? 'successClaimed' : 'claimed'
      } else {
        return visit.visitType === VisitTypes.success ? 'successAvailable' : 'available'
      }
    }
  }, [])

  const { claimVisit } = useSelectCrisisStream()

  /* Storybook hotfix; otherwise crashes; */
  if (!provider) {
    return null
  }

  const { visitQueue } = provider

  const handleCrisisClaim = async (queuedCrisisVisit: PendingVisit) => {
    const crisisVisit = await claimVisit.mutateAsync({
      providerId: provider.providerId,
      providerName: provider.fullName,
      claimedById: provider.providerId,
      claimedBy: 'provider',
      claimedByName: provider.fullName,
      // @ts-ignore
      // TODO: Remove ignore once PendingVisit modal has better typing
      eventStreamId: queuedCrisisVisit.eventStreamId,
    })

    // Merge is used for all other visits in the queue, but claiming a crisis visit returns a stream, not a visit.
    // Only merging the providerId is enough for the UI to work.
    // Needs improvement.
    queuedCrisisVisit.merge({
      providerId: crisisVisit.stream.providerId,
    })
  }

  return (
    <div className="flex justify-center flex-col overflow-initial lg:flex-row lg:overflow-auto">
      <div className="min-w-[35vw] sm:max-lg:min-w-[initial] lg:ml-4 xl:mr-10">
        <Observer>
          {() => {
            const queuedCrisisVisit = !visitQueue.hasInProgressVisit ? visitQueue.queuedCrisisVisits?.[0] || null : null
            return (
              queuedCrisisVisit && (
                <PatientCard
                  className="m-6"
                  pendingVisitId={queuedCrisisVisit.pendingVisitId}
                  onClaim={() => handleCrisisClaim(queuedCrisisVisit)}
                  variant={queuedCrisisVisit.claimed ? 'crisisClaimed' : VisitTypes.crisis}
                  key={queuedCrisisVisit.pendingVisitId}
                  name={queuedCrisisVisit.memberFullName}
                  age={queuedCrisisVisit.age}
                  state={queuedCrisisVisit.fullLocation}
                  modality={queuedCrisisVisit.modality}
                  waitTime={queuedCrisisVisit.waitTime}
                  shouldHideClock={queuedCrisisVisit.shouldHideClock}
                  showAslPill={queuedCrisisVisit.shouldShowAslInterpreterPill}
                />
              )
            )
          }}
        </Observer>
        <Observer>
          {() => {
            const visit = visitQueue.hasInProgressVisit
              ? visitQueue.inProgressVisits[0]
              : visitQueue.queuedTalkNowAndMedicalVisits?.[0] || null

            return (
              visit && (
                <PatientCard
                  className="m-6"
                  pendingVisitId={visit.pendingVisitId}
                  onClaim={visit.claim}
                  variant={getVariant(visit)}
                  key={visit.pendingVisitId}
                  name={visit.memberFullName}
                  age={visit.age}
                  state={visit.fullLocation}
                  modality={visit.modality}
                  waitTime={visit.waitTime}
                  shouldHideClock={visit.shouldHideClock}
                  showAslPill={visit.shouldShowAslInterpreterPill}
                />
              )
            )
          }}
        </Observer>
        <Observer>
          {() => {
            const assignedVisits = visitQueue.hasInProgressVisit
              ? visitQueue?.assignedVisits
              : visitQueue.assignedVisits.slice(1)

            return assignedVisits.map(
              ({
                memberFullName,
                pendingVisitId,
                age,
                fullLocation,
                modality,
                shouldHideClock,
                waitTime,
                wasSentToReview,
              }) => (
                <PatientCard
                  className="m-6"
                  pendingVisitId={pendingVisitId}
                  variant={'assigned'}
                  key={pendingVisitId}
                  name={memberFullName}
                  age={age}
                  state={fullLocation}
                  modality={modality}
                  waitTime={waitTime}
                  wasSentToReview={wasSentToReview}
                  shouldHideClock={shouldHideClock}
                />
              ),
            )
          }}
        </Observer>
        <Observer>
          {() =>
            visitQueue.inReviewVisits.map(
              ({
                memberFullName,
                pendingVisitId,
                age,
                fullLocation,
                modality,
                shouldHideClock,
                waitTime,
                wasSentToReview,
                shouldShowAslInterpreterPill,
              }) => (
                <PatientCard
                  className="m-6"
                  pendingVisitId={pendingVisitId}
                  variant={'in-review'}
                  key={pendingVisitId}
                  name={memberFullName}
                  age={age}
                  state={fullLocation}
                  modality={modality}
                  waitTime={waitTime}
                  wasSentToReview={wasSentToReview}
                  shouldHideClock={shouldHideClock}
                  showAslPill={shouldShowAslInterpreterPill}
                />
              ),
            )
          }
        </Observer>
        <Observer>
          {() =>
            visitQueue.scheduledPendingVisits
              ?.slice(0, 1)
              ?.map((visit) => (
                <ScheduleCard
                  className="m-6"
                  key={visit.pendingVisitId}
                  pendingVisitId={visit.pendingVisitId}
                  date={visit.scheduledAt}
                  patientFullName={visit.memberFullName}
                  age={visit.age}
                  visitReason={visit.reasonForVisit}
                  location={visit.fullLocation}
                  modality={visit.modality}
                  shouldDisableVisitPage={visit.shouldDisableVisitPage}
                  isSuccess={visit.isSuccess}
                  showAslPill={visit.shouldShowAslInterpreterPill}
                />
              )) || null
          }
        </Observer>
      </div>

      <div className="sm:max-md:flex sm:max-md:flex-wrap sm:max-md:justify-around sm:max-md:[&_>_div]:max-w-[40%]">
        <Observer>
          {() =>
            provider.phoneToVerify && (
              <Alert
                type={'error'}
                title={'Phone Verification Needed'}
                Icon={PhoneContained}
                buttonText={'Send SMS'}
                onClick={async () => {
                  await provider.sendVerification(provider.phoneToVerify.id)
                  Modals.open('phoneVerification', {
                    provider,
                  })
                }}
              >
                Verify your phone number to receive SMS messages.
              </Alert>
            )
          }
        </Observer>

        <Alert type={'info'} title={'Need Help?'} reviewLink={HELP_CENTER} reviewLinkText="Get Help">
          Visit our Help Center to contact our support team.
        </Alert>

        <Observer>
          {() =>
            provider?.expiredLicenses?.map(({ id, state, type }) => (
              <Alert key={id} type={'error'} title={'License Expiring'} Icon={MedicalHistory}>
                Your {statesByValue[state] || ''} {type} license is expiring shortly.
              </Alert>
            )) || null
          }
        </Observer>
        <Observer>
          {() =>
            provider.isRxChangeAvailable ? (
              <Alert type={'info'} title={'RX Change Requests'} reviewLink={'/rxchanges'} Icon={MedicalHistory}>
                Review and approve or cancel these requests
              </Alert>
            ) : null
          }
        </Observer>

        <Observer>
          {() =>
            /* timestamp is not unique, so using i as key */
            !User.isHidePrescriptionsFeatureFlagEnabled
              ? provider?.transmissionErrorsList?.map((transmissionError, i) => (
                  <TransmissionError key={i} transmissionError={transmissionError} />
                )) || null
              : null
          }
        </Observer>

        <Observer>
          {() =>
            provider?.resources?.map(({ resourceId, title, body }) => (
              <AdditionalInfoCard key={resourceId} title={title} text={body} />
            )) || null
          }
        </Observer>
      </div>
    </div>
  )
}

export default observer(Home)
